// A variant's difference from its plan as chips (the project's Costs tab): price (paid more per part), usage (used more
// than the BOM: scrap, rejects), fees (the CM's fee, labour, packing), freight & tariffs, volume (one-time costs over
// fewer units), other and set aside, from shared/costs.ts runResult(). A run's own Result tab became Landed cost
// (RunLanded.tsx; Mitch, 2026-11-04: "I don't really understand the purpose of actual vs plan").

import { type ReactNode, useState } from "react";
import { knownUnitCost, type RunResult, type RunUnits, runResult, type Stack, type Variance } from "../../../../../shared/costs.ts";
import { runIntlUnits, runPresales } from "../../../../../shared/presales.ts";
import { LAYER_LABELS, LAYERS } from "../../../../../shared/runs.ts";
import { centsText, signedCents, usd4, useCostIndex } from "../../../lib/costs.ts";
import { qty } from "../../../lib/format.ts";
import { usePresaleIndex } from "../../../lib/presales.ts";
import { Badge, cx } from "../../../ui/controls.tsx";
import { type Column, DataTable } from "../../../ui/DataTable.tsx";
import { Figure, Figures } from "../../../ui/PageParts.tsx";
import { Why } from "../../../ui/Why.tsx";
import { Card } from "../Workspace.tsx";
import { Legend, StackBar } from "./charts.tsx";
import { stackLines } from "./RunPlan.tsx";
import type { RunProps } from "./RunsTab.tsx";

const PARTS = [
  ["price", "Price"],
  ["usage", "Usage"],
  ["fees", "Fees"],
  ["freight", "Freight & tariffs"],
  ["volume", "Volume"],
  ["other", "Other"],
  ["setAside", "Set aside"],
] as const;

/** One part of a variance as a chip: red when it costs more, green when less, with its why. */
export function VarianceChip({ v, part, label }: { v: Variance; part: (typeof PARTS)[number][0]; label: string }) {
  const c = v[part];
  if (c === 0) return null;
  return (
    <Why title={`${label}: ${signedCents(c)} a unit`} note={v.why[part]} width={380}>
      <span
        className={cx(
          "inline-flex h-5 items-center rounded px-1.5 text-[11px] font-medium whitespace-nowrap",
          c > 0 ? "bg-danger-soft text-danger" : "bg-ok-soft text-ok",
        )}
      >
        {label} {signedCents(c)}
      </span>
    </Why>
  );
}

export function VarianceChips({ v }: { v: Variance }) {
  return (
    <span className="flex flex-wrap gap-1">
      {PARTS.map(([k, label]) => (
        <VarianceChip key={k} v={v} part={k} label={label} />
      ))}
      {v.total === 0 && <span className="text-[11px] text-faint">on plan</span>}
    </span>
  );
}
